import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { MapProvider } from '../map/map-provider';
import { GraphicService } from './graphic-service';
import { GraphicTool, GraphicState } from './graphic-state';
import MapView from '@arcgis/core/views/MapView';

const DEFAULT_SYMBOLS: GraphicState.SymbolOptions = {
  fillColor: '#2563eb',
  fillOpacity: 0.15,
  outlineColor: '#2563eb',
  outlineWidth: 2.5,
  textSize: 13,
  textBold: true,
  textItalic: false,
  textHaloColor: '#16a34a',
  textHaloSize: 3,
};

@Injectable()
export class GraphicNotifier extends BaseNotifier<GraphicState> {
  private static readonly INITIAL: GraphicState = {
    activeTool: null,
    items: [],
    symbols: { ...DEFAULT_SYMBOLS },
    selectedId: null,
    pendingText: '',
    isDrawing: false,
    liveLabel: null,
  };

  constructor(ref: Ref) {
    super(ref, GraphicNotifier.INITIAL);

    this.graphicSvc.setOnAdd((id, type, label) => {
      this.updateState(s => ({
        ...s,
        items: [...s.items, { id, type, label }],
        pendingText: type === 'text' ? '' : s.pendingText,
        isDrawing: false,
        liveLabel: null,
      }));
    });

    this.graphicSvc.setOnSelect((id) => {
      this.updateState(s => ({ ...s, selectedId: s.selectedId === id ? null : id }));
    });

    this.graphicSvc.setOnLiveUpdate((label) => {
      this.updateState(s => ({ ...s, liveLabel: label }));
    });
  }

  private get mapProvider() { return this.ref.watch(MapProvider); }
  private get graphicSvc() { return this.ref.watch(GraphicService); }

  selectTool(tool: GraphicTool): void {
    const current = this.snapshot().activeTool;
    if (current === tool) {
      this.deactivateTool();
      return;
    }

    this.graphicSvc.deactivate();
    this.graphicSvc.setSymbolOptions(this.snapshot().symbols);

    const view = this.mapProvider.state().view;
    if (!view) return;
    if (!this.graphicSvc.initialized) this.graphicSvc.initialize(view as MapView);

    this.setState({ ...this.snapshot(), activeTool: tool, selectedId: null, isDrawing: false, liveLabel: null });
  }

  startDrawing(): void {
    const state = this.snapshot();
    const tool = state.activeTool;
    if (!tool) return;

    this.updateState(s => ({ ...s, isDrawing: true, liveLabel: null }));

    this.graphicSvc.setSymbolOptions(state.symbols);
    const textContent = state.pendingText || 'Nota';
    this.graphicSvc.activateTool(tool, textContent);
  }

  selectItem(id: number): void {
    this.graphicSvc.zoomTo(id);
    this.updateState(s => ({ ...s, selectedId: s.selectedId === id ? null : id }));
  }

  setPendingText(text: string): void {
    this.updateState(s => ({ ...s, pendingText: text }));
    this.graphicSvc.setTextContent(text || 'Nota');
  }

  updateSymbols(partial: Partial<GraphicState.SymbolOptions>): void {
    const merged = { ...this.snapshot().symbols, ...partial };
    this.updateState(s => ({ ...s, symbols: merged }));
    this.graphicSvc.setSymbolOptions(merged);
  }

  removeItem(id: number): void {
    this.graphicSvc.removeItem(id);
    this.updateState(s => ({
      ...s,
      items: s.items.filter(i => i.id !== id),
      selectedId: s.selectedId === id ? null : s.selectedId,
    }));
  }

  clearAll(): void {
    this.graphicSvc.clearAll();
    this.updateState(s => ({ ...s, items: [], activeTool: null, selectedId: null, isDrawing: false, liveLabel: null }));
    this.graphicSvc.deactivate();
  }

  cancelDrawing(): void {
    this.graphicSvc.deactivate();
    this.updateState(s => ({ ...s, isDrawing: false, liveLabel: null }));
  }

  deactivateTool(): void {
    this.updateState(s => ({ ...s, activeTool: null, selectedId: null, isDrawing: false, liveLabel: null }));
    this.graphicSvc.deactivate();
  }
}
